<template>
  <div class="app-container">
    <!-- 搜索区域 -->
    <el-form :inline="true" :model="query" class="search-form">
      <el-form-item label="关键词">
        <el-input v-model="query.keyword" placeholder="平台名称/编码" clearable @keyup.enter="fetchData" />
      </el-form-item>
      <el-form-item label="分类">
        <el-select v-model="categoryFilter" placeholder="请选择分类" clearable @change="fetchData">
          <el-option label="电商平台" value="电商平台" />
          <el-option label="物流平台" value="物流平台" />
          <el-option label="支付平台" value="支付平台" />
        </el-select>
      </el-form-item>
      <el-form-item label="状态">
        <el-select v-model="statusFilter" placeholder="请选择状态" clearable @change="fetchData">
          <el-option label="启用" :value="true" />
          <el-option label="禁用" :value="false" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" icon="Search" @click="fetchData">查询</el-button>
        <el-button icon="Refresh" @click="resetSearch">重置</el-button>
        <el-button type="success" icon="Plus" @click="handleAdd">新增</el-button>
      </el-form-item>
    </el-form>

    <!-- 表格区域 -->
    <basic-table :query="query" @handler="fetchData">
      <template #platformType="{ row }">
        <el-tag>{{ row.platformType }}</el-tag>
      </template>
      <template #category="{ row }">
        <el-tag>{{ row.category }}</el-tag>
      </template>
      <template #isvMode="{ row }">
        <el-tag :type="row.isvMode ? 'success' : 'info'">{{ row.isvMode ? 'ISV模式' : '普通模式' }}</el-tag>
      </template>
      <template #isEnabled="{ row }">
        <el-switch v-model="row.isEnabled" @change="handleStatusToggle(row)" />
      </template>
      <template #toolbar="{ row }">
        <el-button link type="primary" size="small" icon="View" @click="handleView(row)">查看</el-button>
        <el-button link type="primary" size="small" icon="Edit" @click="handleEdit(row)">编辑</el-button>
        <el-button link type="danger" size="small" icon="Delete" @click="handleDelete(row)">删除</el-button>
      </template>
    </basic-table>

    <!-- 新增/编辑对话框 -->
    <el-dialog v-model="dialogVisible" :title="dialogTitle" width="800px" @close="handleDialogClose">
      <el-form ref="formRef" :model="formData" :rules="formRules" label-width="120px" autocomplete="off">
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="选择平台" prop="platformCode">
              <el-select v-model="formData.platformCode" placeholder="请选择平台" style="width: 100%" 
                :disabled="isEdit" @change="handlePlatformSelect" filterable>
                <el-option v-for="item in supportedPlatforms" :key="item.code" 
                  :label="item.name" :value="item.code">
                  <span>{{ item.name }}</span>
                  <span style="float: right; color: #8492a6; font-size: 13px">{{ item.code }}</span>
                </el-option>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="平台名称">
              <el-input v-model="formData.platformName" placeholder="自动填充" disabled autocomplete="off" />
            </el-form-item>
          </el-col>
        </el-row>
        <!-- 平台配置提示 -->
        <el-alert v-if="platformConfigTip" :title="platformConfigTip" type="info" :closable="false" 
          style="margin-bottom: 20px" show-icon />
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="平台类型" prop="platformType">
              <el-select v-model="formData.platformType" placeholder="请选择" style="width: 100%">
                <el-option label="B2C" value="B2C" />
                <el-option label="C2C" value="C2C" />
                <el-option label="B2B2C" value="B2B2C" />
                <el-option label="B2B" value="B2B" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="分类" prop="category">
              <el-select v-model="formData.category" placeholder="请选择" style="width: 100%">
                <el-option label="电商平台" value="电商平台" />
                <el-option label="物流平台" value="物流平台" />
                <el-option label="支付平台" value="支付平台" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="22">
            <el-form-item label="API地址" prop="apiUrl">
              <el-input v-model="formData.apiUrl" placeholder="https://api.example.com" autocomplete="off" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="API版本" prop="apiVersion">
              <el-input v-model="formData.apiVersion" placeholder="如：2.0" autocomplete="off" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="是否启用">
              <el-switch v-model="formData.isEnabled" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="应用Key" prop="appKey">
              <el-input v-model="formData.appKey" placeholder="应用密钥Key" autocomplete="off" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="应用密钥">
              <el-input v-model="formData.appSecret" type="password" placeholder="应用密钥Secret" show-password autocomplete="new-password" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="排序码">
              <el-input-number v-model="formData.sortCode" :min="0" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="ISV模式">
              <el-switch v-model="formData.isvMode" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row v-if="formData.isvMode" :gutter="20">
          <el-col :span="12">
            <el-form-item label="ISV应用Key">
              <el-input v-model="formData.isvAppKey" placeholder="ISV服务商应用Key" autocomplete="off" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="ISV应用密钥">
              <el-input v-model="formData.isvAppSecret" type="password" placeholder="ISV服务商应用密钥" show-password autocomplete="new-password" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="扩展配置">
          <el-input v-model="formData.extraConfig" type="textarea" :rows="3" placeholder="JSON格式的扩展配置" autocomplete="off" />
        </el-form-item>
        <el-form-item label="描述">
          <el-input v-model="formData.description" type="textarea" :rows="2" placeholder="平台描述" autocomplete="off" />
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="formData.remark" type="textarea" :rows="2" placeholder="备注信息" autocomplete="off" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSubmit">确定</el-button>
      </template>
    </el-dialog>

    <!-- 查看详情对话框 -->
    <el-dialog v-model="viewDialogVisible" title="平台配置详情" width="800px">
      <el-form label-width="120px">
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="平台编码">
              <span>{{ viewData.platformCode }}</span>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="平台名称">
              <span>{{ viewData.platformName }}</span>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="平台类型">
              <el-tag>{{ viewData.platformType }}</el-tag>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="分类">
              <el-tag>{{ viewData.category }}</el-tag>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="22">
            <el-form-item label="API地址">
              <span>{{ viewData.apiUrl }}</span>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="API版本">
              <span>{{ viewData.apiVersion }}</span>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="是否启用">
              <el-tag :type="viewData.isEnabled ? 'success' : 'danger'">{{ viewData.isEnabled ? '启用' : '禁用' }}</el-tag>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="应用Key">
              <span>{{ viewData.appKey }}</span>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="应用密钥">
              <span v-if="showSecret">{{ viewData.appSecret }}</span>
              <span v-else>********</span>
              <el-button link type="primary" size="small" @click="showSecret = !showSecret">
                {{ showSecret ? '隐藏' : '显示' }}
              </el-button>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="排序码">
              <span>{{ viewData.sortCode }}</span>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="ISV模式">
              <el-tag :type="viewData.isvMode ? 'success' : 'info'">{{ viewData.isvMode ? 'ISV模式' : '普通模式' }}</el-tag>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row v-if="viewData.isvMode" :gutter="20">
          <el-col :span="12">
            <el-form-item label="ISV应用Key">
              <span>{{ viewData.isvAppKey }}</span>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="ISV应用密钥">
              <span v-if="showIsvSecret">{{ viewData.isvAppSecret }}</span>
              <span v-else>********</span>
              <el-button link type="primary" size="small" @click="showIsvSecret = !showIsvSecret">
                {{ showIsvSecret ? '隐藏' : '显示' }}
              </el-button>
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="扩展配置">
          <pre style="margin: 0; white-space: pre-wrap; word-break: break-all;">{{ viewData.extraConfig }}</pre>
        </el-form-item>
        <el-form-item label="描述">
          <span>{{ viewData.description }}</span>
        </el-form-item>
        <el-form-item label="备注">
          <span>{{ viewData.remark }}</span>
        </el-form-item>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="创建时间">
              <span>{{ this.$util.fmtDate(viewData.createDate) }}</span>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="创建人">
              <span>{{ viewData.createUserName }}</span>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="修改时间">
              <span>{{ this.$util.fmtDate(viewData.updateDate) }}</span>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="修改人">
              <span>{{ viewData.updateUserName }}</span>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
    </el-dialog>
  </div>
</template>

<script>
import BasicTable from '@/components/basic-table.vue'
import { getSupportedPlatforms } from '@/api/platform'

export default {
  name: 'PlatformConfig',
  components: {
    BasicTable
  },
  data() {
    return {
      query: {
        keyword: '',
        filters: [],
        sidx: 'SortCode',
        sord: 'ascending',
        page: 1,
        rows: 20,
        data: [],
        columns: [
          { type: 'selection', width: '55' },
          { type: 'index', label: '序号', width: '60' },
          { prop: 'platformCode', label: '平台编码', width: '130', sortable: true, searchable: true },
          { prop: 'platformName', label: '平台名称', width: '150', sortable: true, searchable: true },
          { type: 'template', prop: 'platformType', label: '平台类型', width: '100' },
          { type: 'template', prop: 'category', label: '分类', width: '110' },
          { prop: 'apiUrl', label: 'API地址', minWidth: '200', showOverflowTooltip: true },
          { prop: 'apiVersion', label: 'API版本', width: '100' },
          { type: 'template', prop: 'isvMode', label: '模式', width: '110' },
          { type: 'template', prop: 'isEnabled', label: '状态', width: '80' },
          { prop: 'sortCode', label: '排序', width: '80', sortable: true },
          { prop: 'createDate', label: '创建时间', width: '160', sortable: true },
          { prop: 'createUserName', label: '创建人', width: '110' },
          { type: 'toolbar', label: '操作', width: '220', fixed: 'right' }
        ]
      },
      categoryFilter: '',
      statusFilter: '',
      supportedPlatforms: [],
      platformConfigTip: '',
      dialogVisible: false,
      viewDialogVisible: false,
      isEdit: false,
      formData: {},
      viewData: {},
      showSecret: false,
      showIsvSecret: false,
      formRules: {
        platformCode: [
          { required: true, message: '请选择平台', trigger: 'change' }
        ],
        platformType: [{ required: true, message: '请选择平台类型', trigger: 'change' }],
        category: [{ required: true, message: '请选择分类', trigger: 'change' }],
        apiUrl: [
          { required: true, message: '请输入API地址', trigger: 'blur' },
          { type: 'url', message: '请输入正确的URL格式', trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    dialogTitle() {
      return this.isEdit ? '编辑平台配置' : '新增平台配置'
    }
  },
  mounted() {
    this.$kaung.init(this.$options.name)
    this.loadSupportedPlatforms()
    this.fetchData()
  },
  methods: {
    loadSupportedPlatforms() {
      getSupportedPlatforms().then(res => {
        this.supportedPlatforms = res.data || []
      })
    },
    builder() {
      let { data, records, total, ...params } = { ...this.query }
      
      // 重置并添加筛选条件
      params.filters = []
      if (this.categoryFilter) {
        params.filters.push({ name: 'category', value: this.categoryFilter, operate: 0 })
      }
      if (this.statusFilter !== '') {
        params.filters.push({ name: 'isEnabled', value: this.statusFilter, operate: 0 })
      }
      return params
    },
    fetchData() {
      this.$kaung.search(this.builder()).then(res => {
        this.query = { ...res.data }
      })
    },
    resetSearch() {
      this.query.keyword = ''
      this.categoryFilter = ''
      this.statusFilter = ''
      this.fetchData()
    },
    handlePlatformSelect(code) {
      const platform = this.supportedPlatforms.find(p => p.code === code)
      if (platform) {
        this.formData.platformName = platform.name
        
        // 如果有默认配置，自动填充
        if (platform.defaultConfig) {
          // 自动填充API地址
          if (platform.defaultConfig.url) {
            this.formData.apiUrl = platform.defaultConfig.url
          }
          // 自动填充备注
          if (platform.defaultConfig.remark) {
            this.formData.remark = platform.defaultConfig.remark
          }
          // 显示配置提示
          this.platformConfigTip = `已自动填充 ${platform.name} 的默认配置，您可以根据需要进行调整`
        } else {
          this.platformConfigTip = ''
        }
      }
    },
    handleAdd() {
      this.isEdit = false
      this.platformConfigTip = ''
      this.formData = {
        platformCode: '',
        platformName: '',
        platformType: 'B2C',
        apiUrl: '',
        apiVersion: '',
        appKey: '',
        appSecret: '',
        isvAppKey: '',
        isvAppSecret: '',
        isvMode: false,
        extraConfig: '',
        isEnabled: true,
        category: '电商平台',
        description: '',
        remark: '',
        sortCode: 0
      }
      this.dialogVisible = true
    },
    handleEdit(row) {
      this.isEdit = true
      this.platformConfigTip = ''
      this.$kaung.getone(row.id).then(res => {
        this.formData = { ...res.data }
        this.dialogVisible = true
      })
    },
    handleView(row) {
      this.$kaung.getone(row.id).then(res => {
        this.viewData = { ...res.data }
        this.showSecret = false
        this.showIsvSecret = false
        this.viewDialogVisible = true
      })
    },
    handleDelete(row) {
      this.$confirm('确定要删除该平台配置吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.$kaung.delete(row.id).then(() => {
          this.$message.success('删除成功')
          this.fetchData()
        })
      })
    },
    handleStatusToggle(row) {
      this.$kaung.submit({ id: row.id, data: { isEnabled: row.isEnabled } }).then(() => {
        this.$message.success('状态更新成功')
      }).catch(() => {
        row.isEnabled = !row.isEnabled
      })
    },
    handleSubmit() {
      this.$refs.formRef.validate(valid => {
        if (!valid) return
        
        const submitData = { ...this.formData }
        // 移除只读字段
        delete submitData.deleteMark
        delete submitData.createDate
        delete submitData.createUserName
        delete submitData.updateDate
        delete submitData.updateUserName
        
        this.$kaung.submit({ id: submitData.id, data: submitData }).then(() => {
          this.$message.success(this.isEdit ? '修改成功' : '新增成功')
          this.dialogVisible = false
          this.fetchData()
        })
      })
    },
    handleDialogClose() {
      this.platformConfigTip = ''
      this.$refs.formRef?.resetFields()
    }
  }
}
</script>

<style scoped>
.app-container {
  padding: 20px;
}
.search-form {
  background: #fff;
  padding: 20px;
  margin-bottom: 20px;
  border-radius: 4px;
}
</style>
